import { useSearchParams } from 'react-router-dom';
import { FcdLangContext, FCD_TEXT, useFcdLangState } from './fcdText';
import { MonthlyTab } from './MonthlyTab';
import { ReportTab } from './ReportTab';
import { TodayTab } from './TodayTab';
import '../wireframe.css';
import '../ultimateDesign.css';
import './fcDashboard.css';

export type FcDashboardTab = 'today' | 'logistics' | 'monthly';

const TAB_KEYS: readonly FcDashboardTab[] = ['today', 'logistics', 'monthly'];

/** The tab in the address, so a link opens the same tab; anything else opens Today. */
export const tabOf = (value: string | null): FcDashboardTab =>
  TAB_KEYS.includes(value as FcDashboardTab) ? (value as FcDashboardTab) : 'today';

/** Fulfillment › FC Dashboard (SSD-7602): the center's numbers in one place, Today first. Read-only. */
export default function FcDashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [lang, setLang] = useFcdLangState();
  const text = FCD_TEXT[lang];
  const tab = tabOf(searchParams.get('tab'));

  const choose = (next: FcDashboardTab) =>
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (next === 'today') {
          params.delete('tab');
        } else {
          params.set('tab', next);
        }
        return params;
      },
      { replace: true },
    );

  return (
    <FcdLangContext.Provider value={lang}>
      <div className="wms-wireframe ff-ultimate wms-fc-dashboard" lang={lang}>
        <div className="fcd-head">
          <div className="ptitle">
            <h2>FC Dashboard</h2>
          </div>
          <div className="fcd-lang" role="group" aria-label={text.langGroup}>
            {(['ko', 'en'] as const).map((option) => (
              <button
                type="button"
                key={option}
                lang={option}
                aria-pressed={lang === option}
                className={lang === option ? 'on' : undefined}
                onClick={() => setLang(option)}
              >
                {option === 'ko' ? '한국어' : 'English'}
              </button>
            ))}
          </div>
        </div>
        <div className="viewtabs" role="tablist" aria-label={text.tablist}>
          {TAB_KEYS.map((key) => (
            <button
              type="button"
              key={key}
              role="tab"
              id={`fcd-tab-${key}`}
              aria-controls="fcd-tabpanel"
              aria-selected={key === tab}
              className={key === tab ? 'vtab on' : 'vtab'}
              onClick={() => choose(key)}
            >
              {text.tabs[key]}
            </button>
          ))}
        </div>
        <div role="tabpanel" id="fcd-tabpanel" aria-labelledby={`fcd-tab-${tab}`}>
          {tab === 'today' && <TodayTab />}
          {tab === 'logistics' && <ReportTab />}
          {tab === 'monthly' && <MonthlyTab />}
        </div>
      </div>
    </FcdLangContext.Provider>
  );
}
